/**
 * A call's review (docs/plans/oncall-line-plan.md 5, 7.5, 7.7): the light
 * new -> reviewed state with a note and the reviewer, so every after-hours
 * contact gets documented in the participant's source record, including any
 * adverse event it reports. Any entitled team member may review (plan 16,
 * WP-10).
 *
 *   Needs review   "Mark reviewed" saves the note and marks it reviewed
 *   Reviewed       "Save note" keeps it reviewed with the new note;
 *                  "Reopen" puts it back to Needs review (the note is kept)
 *
 * PATCH /calls/:id/review answers the call summary plus review_note, which
 * the drawer merges into what it shows. Nothing here reads PHI.
 */
import { useEffect, useState } from 'react';
import { Check, Loader2, RotateCcw } from 'lucide-react';
import type { OncallCallDetail, OncallCallSummary } from '../../api/oncallApi';
import { apiErrorMessage, reviewCall } from '../../api/oncallApi';
import { formatDateTime } from '../../lib/format';
import { fieldCls, primaryBtnCls, secondaryBtnCls } from '../../../ui/styles';
import { useToast } from '../../../ui/Toast';
import { REVIEW_NOTE_MAX } from './callMeta';

type Reviewed = OncallCallSummary & { review_note?: string | null };

interface ReviewFormProps {
  call: OncallCallDetail;
  timeZone: string | null | undefined;
  /** The call after a saved review (summary + review_note). */
  onSaved: (updated: Reviewed) => void;
}

export default function ReviewForm({ call, timeZone, onSaved }: ReviewFormProps) {
  const { toast } = useToast();
  const [note, setNote] = useState(call.review_note ?? '');
  const [saving, setSaving] = useState<null | 'reviewed' | 'new'>(null);

  // A new call, or the same call saved elsewhere, resets the draft.
  useEffect(() => {
    setNote(call.review_note ?? '');
  }, [call.id, call.review_note]);

  const reviewed = call.review_status === 'reviewed';
  const noteChanged = note.trim() !== (call.review_note ?? '').trim();
  const tooLong = note.length > REVIEW_NOTE_MAX;

  const save = async (status: 'reviewed' | 'new', withNote: boolean) => {
    if (saving || tooLong) return;
    setSaving(status);
    try {
      const updated = await reviewCall(call.id, withNote ? { review_status: status, review_note: note.trim() || null } : { review_status: status });
      onSaved(updated as Reviewed);
      toast(status === 'reviewed' ? 'Call marked reviewed' : 'Call reopened for review', { type: 'success' });
    } catch (err) {
      toast(apiErrorMessage(err, 'The review could not be saved'), { type: 'error' });
    } finally {
      setSaving(null);
    }
  };

  const noteId = `oncall-review-note-${call.id}`;

  return (
    <div className="space-y-2" data-testid="oncall-review">
      {reviewed && (
        <p className="text-sm text-gray-600 dark:text-gray-300" data-testid="oncall-reviewed-by">
          Reviewed{call.reviewed_by_name ? ` by ${call.reviewed_by_name}` : ''}
          {call.reviewed_at ? ` on ${formatDateTime(call.reviewed_at, timeZone)}` : ''}.
        </p>
      )}
      <label htmlFor={noteId} className="block text-xs font-medium text-gray-600 dark:text-gray-300">
        Review note
      </label>
      <textarea
        id={noteId}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={3}
        placeholder="What was the call about, and where it was documented"
        className={`${fieldCls} w-full resize-y`}
        aria-describedby={`${noteId}-count`}
        data-testid="oncall-review-note"
      />
      <p
        id={`${noteId}-count`}
        className={`text-right text-xs tabular-nums ${tooLong ? 'text-red-700 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}
      >
        {note.length.toLocaleString()} / {REVIEW_NOTE_MAX.toLocaleString()}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {reviewed ? (
          <>
            <button
              type="button"
              onClick={() => save('reviewed', true)}
              disabled={!!saving || tooLong || !noteChanged}
              className={primaryBtnCls}
              data-testid="oncall-review-save"
            >
              {saving === 'reviewed' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Check className="h-4 w-4" aria-hidden="true" />}
              Save note
            </button>
            <button
              type="button"
              onClick={() => save('new', false)}
              disabled={!!saving}
              className={`${secondaryBtnCls} disabled:cursor-not-allowed disabled:opacity-50`}
              data-testid="oncall-review-reopen"
            >
              {saving === 'new' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <RotateCcw className="h-4 w-4" aria-hidden="true" />}
              Reopen
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => save('reviewed', true)}
            disabled={!!saving || tooLong}
            className={primaryBtnCls}
            data-testid="oncall-review-mark"
          >
            {saving === 'reviewed' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Check className="h-4 w-4" aria-hidden="true" />}
            Mark reviewed
          </button>
        )}
      </div>
    </div>
  );
}
